import React from 'react';
import {
  Card,
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TicketProcessingEntry } from '../../../interfaces/processing';

interface CustomerTicketEntryProps {
  processingEntry: TicketProcessingEntry;
}

const formatDateString = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

const CustomerTicketEntry: React.FC<CustomerTicketEntryProps> = ({ processingEntry }) => {
  const createdAtDate = processingEntry ? new Date(processingEntry.created_at) : null;

  return (
    <Box sx={{ padding: '5px', overflow: 'auto' }}>
      <List>
        <ListItem>
          <ListItemText primary="Ticket ID:" secondary={processingEntry.external_id} />
        </ListItem>
        <ListItem>
          <ListItemText primary="Created at:" secondary={createdAtDate?.toLocaleString()} />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Subject:"
            secondary={processingEntry.ticket_data?.ticket_data?.subject ?? 'No Subject available.'}
          />
        </ListItem>
      </List>

      <Divider sx={{ marginY: '20px' }} />
      <Accordion
        defaultExpanded={false}
        style={{ boxShadow: "none" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
        >
          <div style={{display: 'flex', gap: '10px'}}>
            <ExpandMoreIcon />
            <div>Messages</div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          {processingEntry.ticket_data?.comments_data?.comments?.length > 0 ? (
            processingEntry.ticket_data.comments_data.comments.map((comment, index) => {
              const isRequester = comment?.via?.channel === 'email';
              const authorEmail = comment?.via?.source?.from?.address || '';
              const authorFirstName = comment?.via?.source?.from?.name || 'Anonymous';
              const authorName = isRequester ? `${authorFirstName} ${authorEmail}` : 'Agent';
              const readableDate = new Date(comment.created_at).toLocaleString([], {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
              return (
                <Card key={index} sx={{marginBottom: '10px', padding: '10px'}}>
                  <div>
                    <b>{authorName} - {readableDate} ({comment.public ? 'Public' : 'Internal note'})</b>
                  </div>
                  <div style={{whiteSpace: 'pre-line'}}>
                    {comment.body}
                  </div>
                  <Typography variant="caption">{formatDateString(comment.created_at)}</Typography>
                  {Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
                    <ListItem>
                      <ListItemText
                        primary="Attachments:"
                        secondary={
                          <Box>
                            {comment.attachments.map((attachment, index) => (
                              <Box key={index}>
                                <a href={attachment.content_url} target="_blank" rel="noreferrer">
                                  {attachment.file_name}
                                </a>
                              </Box>
                            ))}
                          </Box>
                        }
                      />
                    </ListItem>
                  )}
                </Card>
              )
            })
          ) : (
            <Typography>No comments available</Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CustomerTicketEntry;
